import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/errorMessage";
import { CheckCircle2, AlertTriangle, Truck, Lock } from "lucide-react";
import { ConferenciaForm, limparRascunhoConferencia } from "@/components/logistica/ConferenciaForm";

// Pagina PUBLICA (QR do FL-05, token unico) — o recebedor na obra NAO precisa de login.
export default function ConferenciaPublicaPage() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [erro, setErro] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [falha, setFalha] = useState(false);
  const pendente = useRef(null);

  useEffect(() => {
    axios.get(`${API}/public/romaneio/${id}`).then(res => setR(res.data))
      .catch(err => setErro(extractErrorMessage(err, "Romaneio não encontrado")));
  }, [id]);

  const enviar = async (payload) => {
    setEnviando(true);
    try {
      const res = await axios.post(`${API}/public/romaneio/${id}/conferir`, payload);
      pendente.current = null;
      setFalha(false);
      setResultado(res.data);
      if (r?.id) limparRascunhoConferencia(r.id);
    } catch (err) {
      if (!err.response || err.response.status >= 500) {
        // Sem conexao / erro do servidor: marcacoes ficam salvas e reenviamos quando a internet voltar
        pendente.current = payload;
        setFalha(true);
      } else {
        toast.error(extractErrorMessage(err, "Erro ao registrar conferência"));
      }
    }
    finally { setEnviando(false); }
  };

  useEffect(() => {
    const reenviar = () => { if (pendente.current) enviar(pendente.current); };
    window.addEventListener("online", reenviar);
    return () => window.removeEventListener("online", reenviar);
  });

  if (erro) return <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6 text-center text-slate-600">{erro}</div>;
  if (!r) return <div className="min-h-screen flex items-center justify-center bg-slate-100 text-slate-400">Carregando…</div>;

  // Tela cheia verde de sucesso
  if (resultado) return (
    <div className="min-h-screen bg-emerald-600 flex items-center justify-center p-6" data-testid="tela-sucesso">
      <div className="text-center text-white space-y-4 max-w-sm">
        <CheckCircle2 className="h-24 w-24 mx-auto" strokeWidth={1.5} />
        <h1 className="text-2xl font-bold" data-testid="sucesso-titulo">Recebimento registrado às {resultado.hora}</h1>
        <div className="bg-white/15 rounded-xl p-4 space-y-1 text-sm">
          <p className="font-semibold">Romaneio {resultado.numero}</p>
          <p>{resultado.obra_titulo}</p>
          <p>Recebido por {resultado.recebedor_nome}</p>
        </div>
        <div className={`inline-block rounded-full px-4 py-1.5 text-sm font-bold ${resultado.itens_div > 0 ? "bg-orange-500" : "bg-white/20"}`} data-testid="sucesso-resumo">
          {resultado.itens_ok} {resultado.itens_ok === 1 ? "item" : "itens"} OK{resultado.itens_div > 0 ? ` · ${resultado.itens_div} com divergência` : ""}
        </div>
        {resultado.itens_div > 0 && (
          <p className="text-xs text-white/85">Logística e Gerente Operacional já foram avisados. Registre no FL-01 físico e fotografe antes de o condutor sair.</p>
        )}
        <p className="text-xs text-white/70 flex items-center justify-center gap-1"><Lock className="h-3 w-3" /> Este link agora é somente leitura. Obrigado!</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100 pb-10" data-testid="conferencia-publica-page">
      <div className="bg-luvcorp-navy text-white px-4 py-4">
        <div className="max-w-xl mx-auto">
          <p className="text-luvcorp-gold text-xs font-bold uppercase tracking-wider flex items-center gap-1"><Truck className="h-4 w-4" /> FL-05 · Conferência de Entrega</p>
          <h1 className="text-xl font-bold">Romaneio {r.numero}</h1>
          <p className="text-sm text-white/70">{r.obra_titulo} · {r.veiculo_placa} · Condutor: {r.condutor_nome}</p>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 mt-4 space-y-3">
        {r.read_only ? (
          <div className="rounded-xl p-5 bg-white border border-slate-200 space-y-3" data-testid="conferencia-somente-leitura">
            <div className="text-center">
              <Lock className="h-8 w-8 text-slate-400 mx-auto mb-1" />
              <p className="font-semibold text-luvcorp-navy">Conferência concluída — somente leitura</p>
              <p className="text-xs text-slate-500">Recebido por {r.recebedor_nome}{r.recebedor_funcao ? ` (${r.recebedor_funcao})` : ""}{r.conferencia_origem === "papel" ? " · lançada a partir do papel" : ""}</p>
            </div>
            {r.linhas.map((l) => (
              <div key={l.linha_id} className="text-sm border-b border-slate-100 pb-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span>{l.nome}</span>
                  <span className="flex items-center gap-2 text-xs whitespace-nowrap">
                    {l.qtd_recebida}/{l.quantidade} {l.unidade}
                    <Badge className={l.conferencia === "OK" ? "bg-emerald-600 text-white" : "bg-red-600 text-white"}>{l.codigo_div || l.conferencia}</Badge>
                  </span>
                </div>
                {l.fl01_folha ? <span className="block text-[10px] text-slate-400 mt-0.5">FL-01 folha {l.fl01_folha}, linha {l.fl01_linha}</span> : null}
              </div>
            ))}
          </div>
        ) : (
          <>
            {falha && (
              <div className="bg-red-600 text-white rounded-xl p-4 space-y-2 shadow-lg" data-testid="aviso-falha-envio">
                <p className="text-sm font-semibold flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
                  Não foi enviado — suas marcações estão guardadas. Tentaremos de novo quando a internet voltar.
                </p>
                <button onClick={() => pendente.current && enviar(pendente.current)} disabled={enviando}
                  className="w-full bg-white text-red-700 rounded-lg px-4 py-2.5 text-sm font-bold active:scale-95 transition"
                  data-testid="btn-tentar-novamente">
                  {enviando ? "Enviando…" : "Tentar novamente"}
                </button>
              </div>
            )}
            <ConferenciaForm romaneio={r} modo="digital" onSubmit={enviar} enviando={enviando} />
          </>
        )}
        <p className="text-center text-[10px] text-slate-400">OBRASUL · Um Show de Obra · Registro físico obrigatório · FL-05 · rev.02</p>
      </div>
    </div>
  );
}
